import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';

import UserDateFormat from '../../../../../../components/user-date-format';
import { isBillingPlanIdUsageBilledHighVolume } from '../../../../../../enums/billing-plan-ids';
import {
  useGetNonWalletProviderAccessStatus,
  NON_WALLET_ELIGIBLE_TRIAL,
  NON_WALLET_ELIGIBLE_ACTIVE,
  NON_WALLET_ELIGIBLE_PENDING_INACTIVE,
  NON_WALLET_ELIGIBLE_INACTIVE,
} from '../../../../../../hooks/non-wallet-access-hooks';
import {
  useHasNonWalletedStampsEndiciaProviders,
  useNonWalletedAndNonExcludedSellerLabelProviders,
} from '../../../../../../hooks/use-seller-label-providers';
import { useTrackYourCarriersEvent, PageEvents } from '../../../../../../hooks/use-track-your-carriers-event';
import { UserPermissionService } from '../../../../../../services/user-permission-service';
import Global from '../../../../../../store/global-selectors';
import messages from '../../../messages';
import { SubscriptionLink } from '../subscription-link';

/**
 * UI Banner component displayed for users who are not in the "new lineup" world
 * @param {object} props
 * @param {() => void} props.openNonWalletActivationModal
 * @returns {React.JSX.Element | null}
 */
export const CallToActionMessage = ({ openNonWalletActivationModal }) => {
  const nonWalletAccessStatus = useGetNonWalletProviderAccessStatus();

  const nonWalletAndNonExcludedSellerLabelProviders = useNonWalletedAndNonExcludedSellerLabelProviders();
  const { billingPlanId } = useSelector(Global.selectSellerSubscriptionPlanInfo) ?? {};
  const pendingSubscription = useSelector(Global.selectSellerPendingSubscription);
  const trackClickLinkToSubscriptionPage = useTrackYourCarriersEvent(PageEvents.YOUR_CARRIERS_SUBSCRIPTION_LINK_CLICK);
  const onLinkClicked = useCallback(() => trackClickLinkToSubscriptionPage(), [trackClickLinkToSubscriptionPage]);
  const userRoles = useSelector(Global.selectUseruserRoles);
  const hasAccessToSubscriptionPage = UserPermissionService.userHasAccountManagementRole(userRoles);

  // stamps/endicia
  const hasNonWalletedStampsEndiciaProviders = useHasNonWalletedStampsEndiciaProviders();
  const pendingInactiveNonWalletAccessMessage = hasNonWalletedStampsEndiciaProviders
    ? messages.pendingInactiveNonWalletAccessExcludingStamps
    : messages.pendingInactiveNonWalletAccess;
  const inactiveNonWalletAccesssMessage = hasNonWalletedStampsEndiciaProviders
    ? messages.inactiveNonWalletAccessExcludingStamps
    : messages.inactiveNonWalletAccess;

  const shouldHideCallToActionMessage = nonWalletAccessStatus === null || isBillingPlanIdUsageBilledHighVolume(billingPlanId);
  if (shouldHideCallToActionMessage) {
    return null;
  }

  if (nonWalletAccessStatus === NON_WALLET_ELIGIBLE_TRIAL && nonWalletAndNonExcludedSellerLabelProviders.length > 0) {
    return <FormattedMessage {...messages.trialNonWalletAccess} />;
  }

  if (nonWalletAccessStatus === NON_WALLET_ELIGIBLE_ACTIVE) {
    return (
      <FormattedMessage
        {...messages.activatedNonWalletAccess}
        values={{
          link: (chunk) => <SubscriptionLink chunk={chunk} disabled={!hasAccessToSubscriptionPage} onLinkClicked={onLinkClicked} />,
        }}
      />
    );
  }

  if (nonWalletAccessStatus === NON_WALLET_ELIGIBLE_PENDING_INACTIVE) {
    return (
      <FormattedMessage
        {...pendingInactiveNonWalletAccessMessage}
        values={{
          endDate: <UserDateFormat value={pendingSubscription.pendingStartDateTimeUTC} />,
        }}
      />
    );
  }

  if (nonWalletAccessStatus === NON_WALLET_ELIGIBLE_INACTIVE && nonWalletAndNonExcludedSellerLabelProviders.length > 0) {
    return (
      <FormattedMessage
        {...inactiveNonWalletAccesssMessage}
        values={{
          enableYourCarriersLink: (chunk) => (
            <Button onClick={openNonWalletActivationModal} theme='link'>
              {chunk}
            </Button>
          ),
        }}
      />
    );
  }

  return null;
};
